import { Img } from 'openimg/react'
import { useState, useEffect, useRef } from 'react'
import { useFetcher } from 'react-router'
import { toast } from 'sonner'
import { Checkbox } from '#app/components/ui/checkbox.tsx'
import {
	Drawer,
	DrawerContent,
	DrawerHeader,
	DrawerTitle,
	DrawerDescription,
	DrawerFooter,
} from '#app/components/ui/drawer.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Input } from '#app/components/ui/input.tsx'
import { Label } from '#app/components/ui/label.tsx'
import { Textarea } from '#app/components/ui/textarea.tsx'
import { getNoteImgSrc } from '#app/utils/misc.tsx'

interface Exhibition {
	id: string
	title: string
	type: string
	address: string | null
	beginDate: string | null
	endDate: string | null
	openingTimes: string | null
	googleMapsUrl: string | null
	websiteUrl: string | null
	description: string | null
	featuredOnHomepage?: boolean
	imageId?: string | null
	imageObjectKey?: string | null
	imageAltText?: string | null
	image?: {
		id: string
		objectKey: string
		altText: string | null
	} | null
}

interface EditExhibitionDrawerProps {
	isOpen: boolean
	onClose: () => void
	/** null → create mode (empty form, submits intent=create) */
	exhibition: Exhibition | null
}

function toFormState(exhibition: Exhibition | null) {
	return {
		title: exhibition?.title || '',
		type: exhibition?.type || 'exhibition',
		address: exhibition?.address || '',
		beginDate: exhibition?.beginDate
			? new Date(exhibition.beginDate).toISOString().split('T')[0]
			: '',
		endDate: exhibition?.endDate
			? new Date(exhibition.endDate).toISOString().split('T')[0]
			: '',
		openingTimes: exhibition?.openingTimes
			? (JSON.parse(exhibition.openingTimes) as string[]).join('\n')
			: '',
		googleMapsUrl: exhibition?.googleMapsUrl || '',
		websiteUrl: exhibition?.websiteUrl || '',
		description: exhibition?.description || '',
		imageId: exhibition?.imageId || '',
		imageAltText: exhibition?.imageAltText || '',
		// Stored as 'true' | '' so the generic FormData loop in handleSubmit
		// serializes it unchanged; the server parses 'true' → boolean.
		featuredOnHomepage: exhibition?.featuredOnHomepage ? 'true' : '',
	}
}

export function EditExhibitionDrawer({
	isOpen,
	onClose,
	exhibition,
}: EditExhibitionDrawerProps) {
	const isCreate = exhibition === null
	const fetcher = useFetcher()
	const imageFetcher = useFetcher()
	const isSubmitting = fetcher.state !== 'idle'
	const [showImagePicker, setShowImagePicker] = useState(false)
	const lastIntentRef = useRef<'create' | 'update' | 'delete'>(
		isCreate ? 'create' : 'update',
	)

	const [formData, setFormData] = useState(() => toFormState(exhibition))

	const [selectedImage, setSelectedImage] = useState<{
		id: string
		objectKey: string
		altText: string | null
	} | null>(exhibition?.image || null)

	const [imageFile, setImageFile] = useState<File | null>(null)
	const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null)

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0] ?? null
		setImageFile(file)
		setImagePreviewUrl((prev) => {
			if (prev) URL.revokeObjectURL(prev)
			return file ? URL.createObjectURL(file) : null
		})
	}

	useEffect(() => {
		setFormData(toFormState(exhibition))
		setSelectedImage(exhibition?.image || null)
		setImageFile(null)
		setImagePreviewUrl((prev) => {
			if (prev) URL.revokeObjectURL(prev)
			return null
		})
	}, [exhibition])

	useEffect(() => {
		if (showImagePicker && imageFetcher.state === 'idle' && !imageFetcher.data) {
			void imageFetcher.load('/resources/images/list')
		}
	}, [showImagePicker, imageFetcher])

	const handleSubmit = () => {
		const openingTimesArray = formData.openingTimes
			.split('\n')
			.map((line: string) => line.trim())
			.filter((line: string) => line.length > 0)

		const body = new FormData()
		if (isCreate) {
			body.set('intent', 'create')
			lastIntentRef.current = 'create'
		} else {
			body.set('intent', 'update')
			body.set('id', exhibition.id)
			lastIntentRef.current = 'update'
		}
		for (const [key, value] of Object.entries(formData)) {
			body.set(key, value ?? '')
		}
		body.set('openingTimes', JSON.stringify(openingTimesArray))
		if (imageFile) body.set('image', imageFile)

		void fetcher.submit(body, {
			method: 'POST',
			action: '/resources/exhibitions',
			encType: 'multipart/form-data',
		})
	}

	const handleDelete = () => {
		if (!exhibition) return
		if (confirm('Weet je zeker dat je deze expositie/galerie wilt verwijderen?')) {
			lastIntentRef.current = 'delete'
			void fetcher.submit(
				{ intent: 'delete', id: exhibition.id },
				{ method: 'POST', action: '/resources/exhibitions' },
			)
		}
	}

	const [successHandled, setSuccessHandled] = useState(false)
	if (fetcher.data?.success && !successHandled) {
		setSuccessHandled(true)
		if (lastIntentRef.current === 'delete') {
			toast.success('Expositie verwijderd')
		} else if (lastIntentRef.current === 'create') {
			toast.success('Expositie toegevoegd', {
				description:
					'De beschrijving en vertalingen worden op de achtergrond aangevuld.',
			})
		} else {
			toast.success('Expositie bijgewerkt', {
				description: 'De vertalingen worden op de achtergrond bijgewerkt.',
			})
		}
		onClose()
	}

	const availableImages = (imageFetcher.data as { images: Array<{ id: string; objectKey: string; altText: string | null; note?: { title: string } | null }> } | undefined)?.images || []

	// Dates are stored at UTC midnight (see exposities.tsx), so compare the
	// YYYY-MM-DD form value against today's UTC date. An ended event can't be
	// (un)pinned — a stale flag is inert on the homepage anyway.
	const todayStr = new Date().toISOString().split('T')[0]!
	const hasEnded = Boolean(formData.endDate && formData.endDate < todayStr)

	return (
		<Drawer open={isOpen} onOpenChange={onClose}>
			<DrawerContent className="max-h-[90vh]" style={{ background: 'var(--paper)' }}>
				<DrawerHeader>
					<DrawerTitle className="font-heading text-xl font-medium italic">
						{isCreate ? 'Nieuwe' : 'Bewerk'}{' '}
						{formData.type === 'gallery' ? 'galerie' : formData.type === 'museum' ? 'museum' : formData.type === 'public_commission' ? 'opdracht' : 'expositie'}
						{isCreate ? ' toevoegen' : ''}
					</DrawerTitle>
					<DrawerDescription style={{ color: 'var(--muted-gallery)' }}>
						{isCreate
							? 'Vul de details in. Zonder beschrijving wordt er automatisch een geschreven.'
							: 'Pas de details aan'}
					</DrawerDescription>
				</DrawerHeader>

				<div className="space-y-4 overflow-y-auto px-4 pb-4">
					<div>
						<Label htmlFor="title">Titel</Label>
						<Input
							id="title"
							value={formData.title}
							onChange={(e) => setFormData({ ...formData, title: e.target.value })}
						/>
					</div>

					{/* Image: upload a new one, or pick an existing sculpture photo */}
					<div>
						<Label>Afbeelding</Label>
						<div className="mt-2 flex items-center gap-4">
							{imagePreviewUrl || exhibition?.imageObjectKey ? (
								<Img
									src={
										imagePreviewUrl ||
										getNoteImgSrc(exhibition?.imageObjectKey as string)
									}
									alt={formData.imageAltText || formData.title}
									width={192}
									height={192}
									fit="cover"
									className="h-24 w-24 object-cover"
									style={{ borderRadius: 8 }}
								/>
							) : selectedImage ? (
								<div className="relative">
									<Img
										src={getNoteImgSrc(selectedImage.objectKey)}
										alt={selectedImage.altText || ''}
										width={192}
										height={192}
										fit="cover"
										className="h-24 w-24 object-cover"
										style={{ borderRadius: 8 }}
									/>
									<button
										type="button"
										onClick={() => {
											setSelectedImage(null)
											setFormData({ ...formData, imageId: '' })
										}}
										className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full shadow"
										style={{ background: 'var(--paper)', border: '1px solid var(--line)', cursor: 'pointer' }}
									>
										<Icon name="cross-1" className="h-3 w-3" />
									</button>
								</div>
							) : (
								<div
									className="flex h-24 w-24 items-center justify-center"
									style={{ background: 'var(--paper-2)', borderRadius: 8 }}
								>
									<Icon name="camera" className="h-6 w-6" style={{ color: 'var(--muted-2)' }} />
								</div>
							)}
							<div className="flex flex-col gap-2">
								<label
									className="btn-gallery btn-gallery-ghost cursor-pointer text-center text-sm"
									style={{ height: 40, padding: '9px 18px' }}
								>
									{imageFile ? 'Andere foto kiezen' : 'Upload foto'}
									<input
										type="file"
										accept="image/*"
										onChange={handleFileChange}
										className="sr-only"
									/>
								</label>
								<button
									type="button"
									onClick={() => setShowImagePicker(!showImagePicker)}
									className="btn-gallery btn-gallery-ghost text-sm"
									style={{ height: 40, padding: '0 18px' }}
								>
									{showImagePicker ? 'Sluiten' : 'Kies bestaande afbeelding'}
								</button>
							</div>
						</div>

						<div className="mt-3">
							<Label htmlFor="imageAltText">Alt-tekst afbeelding</Label>
							<Input
								id="imageAltText"
								value={formData.imageAltText}
								onChange={(e) =>
									setFormData({ ...formData, imageAltText: e.target.value })
								}
								placeholder="Korte beschrijving van de foto"
							/>
						</div>

						{showImagePicker && (
							<div className="mt-4 grid grid-cols-4 gap-2" style={{ maxHeight: 240, overflowY: 'auto' }}>
								{availableImages.length > 0 ? (
									availableImages.map((img) => (
										<button
											key={img.id}
											type="button"
											onClick={() => {
												setSelectedImage(img)
												setFormData({ ...formData, imageId: img.id })
												setShowImagePicker(false)
											}}
											className="overflow-hidden transition-opacity hover:opacity-80"
											style={{
												aspectRatio: '1',
												border: formData.imageId === img.id ? '2px solid var(--ink)' : '1px solid var(--line)',
												borderRadius: 6,
												padding: 0,
												cursor: 'pointer',
												background: 'none',
											}}
										>
											<Img
												src={getNoteImgSrc(img.objectKey)}
												alt={img.altText || ''}
												width={192}
												height={192}
												fit="cover"
												className="h-full w-full object-cover"
											/>
										</button>
									))
								) : (
									<p className="col-span-4 py-4 text-center text-sm" style={{ color: 'var(--muted-gallery)' }}>
										{imageFetcher.state === 'loading' ? 'Laden...' : 'Geen afbeeldingen beschikbaar'}
									</p>
								)}
							</div>
						)}
					</div>

					<div>
						<Label htmlFor="type">Type</Label>
						<select
							id="type"
							value={formData.type}
							onChange={(e) => setFormData({ ...formData, type: e.target.value })}
							className="w-full border-0 border-b bg-transparent px-0 py-2.5 text-base outline-none"
							style={{ borderColor: 'var(--line)', color: 'var(--ink)', fontFamily: 'var(--font-sans)' }}
						>
							<option value="exhibition">Expositie</option>
							<option value="gallery">Galerie</option>
							<option value="museum">Museum</option>
							<option value="public_commission">Opdracht openbare ruimte</option>
						</select>
					</div>

					<div>
						<Label htmlFor="address">Adres</Label>
						<Input
							id="address"
							value={formData.address}
							onChange={(e) => setFormData({ ...formData, address: e.target.value })}
						/>
					</div>

					<div className="grid grid-cols-2 gap-4">
						<div>
							<Label htmlFor="beginDate">Begindatum</Label>
							<Input
								id="beginDate"
								type="date"
								value={formData.beginDate}
								onChange={(e) => setFormData({ ...formData, beginDate: e.target.value })}
							/>
						</div>
						<div>
							<Label htmlFor="endDate">Einddatum</Label>
							<Input
								id="endDate"
								type="date"
								value={formData.endDate}
								onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
							/>
						</div>
					</div>

					<div>
						<div className="flex items-center gap-3">
							<Checkbox
								id="featuredOnHomepage"
								checked={formData.featuredOnHomepage === 'true'}
								disabled={hasEnded}
								onCheckedChange={(checked) =>
									setFormData({
										...formData,
										featuredOnHomepage: checked === true ? 'true' : '',
									})
								}
							/>
							<Label htmlFor="featuredOnHomepage">
								Uitgelicht op de homepage
							</Label>
						</div>
						<p className="mt-1.5 text-sm" style={{ color: 'var(--muted-gallery)' }}>
							{hasEnded
								? 'Dit event is afgelopen.'
								: 'Toont dit event op de homepage in plaats van andere aankomende events; vervalt automatisch na de einddatum.'}
						</p>
					</div>

					<div>
						<Label htmlFor="openingTimes">Openingstijden (één per regel)</Label>
						<Textarea
							id="openingTimes"
							value={formData.openingTimes}
							onChange={(e) => setFormData({ ...formData, openingTimes: e.target.value })}
							rows={3}
							placeholder="Ma-Vr: 10:00-17:00&#10;Za-Zo: 12:00-17:00"
						/>
					</div>

					<div>
						<Label htmlFor="websiteUrl">Website URL</Label>
						<Input
							id="websiteUrl"
							type="url"
							value={formData.websiteUrl}
							onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
							placeholder="https://..."
						/>
					</div>

					<div>
						<Label htmlFor="googleMapsUrl">Google Maps URL</Label>
						<Input
							id="googleMapsUrl"
							type="url"
							value={formData.googleMapsUrl}
							onChange={(e) => setFormData({ ...formData, googleMapsUrl: e.target.value })}
							placeholder="https://www.google.com/maps/..."
						/>
					</div>

					<div>
						<Label htmlFor="description">Beschrijving</Label>
						<Textarea
							id="description"
							value={formData.description}
							onChange={(e) => setFormData({ ...formData, description: e.target.value })}
							rows={3}
						/>
					</div>
				</div>

				<DrawerFooter>
					<div className="flex w-full justify-between">
						{isCreate ? (
							<span />
						) : (
							<button
								onClick={handleDelete}
								disabled={isSubmitting}
								className="text-sm"
								style={{ background: 'none', border: 'none', color: 'var(--destructive)', cursor: 'pointer' }}
							>
								Verwijderen
							</button>
						)}
						<div className="flex gap-2">
							<button onClick={onClose} className="btn-gallery btn-gallery-ghost text-sm" style={{ height: 40, padding: '0 18px' }}>
								Annuleren
							</button>
							<button
								onClick={handleSubmit}
								disabled={isSubmitting || !formData.title.trim()}
								className="btn-gallery text-sm"
								style={{ height: 40, padding: '0 18px' }}
							>
								{isSubmitting ? 'Opslaan...' : isCreate ? 'Toevoegen' : 'Opslaan'}
							</button>
						</div>
					</div>
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	)
}
